import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Print,
  Download,
  DragIndicator,
  Reorder,
  LinkedIn as LinkedInIcon
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const resumeMeta = {
  hasPhoto: false,
  columns: 2,
};

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultData = {
  firstName: "Richard",
  lastName: "Sanchez",
  currentPosition: "Senior Marketing Manager",
  phone: "+123-456-7890",
  email: "hello@reallygreatsite.com",
  city: "123 Anywhere St., Any City",
  website: "www.reallygreatsite.com",
  profile:
    "Highly creative and results-driven Marketing Manager with over 8 years of experience in developing and executing successful marketing strategies. Expert in digital advertising, brand management, and leading cross-functional teams to exceed business goals.",
};

// Updated: Default sections that can appear on either side
const defaultLeftSectionOrder = [
  'contact',
  'skills',
  'education',
  'languages',
  'websites',
  'accomplishments'
];

const defaultRightSectionOrder = [
  'profile',
  'workExperience',
  'certifications',
  'volunteering',
  'interests',
  'additionalInfo',
  'references'
];

// Updated: Combined list of all possible sections
const allPossibleSections = [
  'contact',
  'skills',
  'education',
  'languages',
  'websites',
  'accomplishments',
  'profile',
  'workExperience',
  'certifications',
  'volunteering',
  'interests',
  'additionalInfo',
  'references'
];

const Resume30 = ({
  color = "#6c5ce7",
  font = "Roboto",
  fontSize = "14px",
  headingSize = "24px",
  fontStyle = "normal",
  sectionSpacing = 50,
  paragraphSpacing = 30,
  lineSpacing = 20,
  topBottomMargin = 40,
  sideMargins = 40,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
  // Only LinkedIn will be used from these
  linkedIn = '',
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  
  // Merge default data with form data - but ONLY LinkedIn will be used from props
  const data = { 
    ...defaultData, 
    ...formData,
    // Ensure LinkedIn is set from the prop
    linkedIn: linkedIn || formData.linkedIn || '',
  };

  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const [sidebarPages, setSidebarPages] = useState([]);
  const mainContentRef = useRef(null);
  const rightContentRef = useRef(null);
  const sidebarContentRef = useRef(null);

  // State for section ordering - separate for left and right
  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  
  // State for arrange sections dialog
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  useEffect(() => {
    // Load data from localStorage
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) {
      setEducationEntries(JSON.parse(storedEntries));
    }

    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) {
      setSavedSkills(JSON.parse(storedSkills));
    }

    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) {
      setSavedSummary(storedSummaries[storedSummaries.length - 1]);
    }

    const storedAdditionalInfo = JSON.parse(localStorage.getItem("additionalInfo")) || [];
    setSavedAdditionalInfo(storedAdditionalInfo);

    const storedLanguages = JSON.parse(localStorage.getItem("languagesList")) || [];
    setSavedLanguages(storedLanguages);

    const storedAccomplishments = JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
    setSavedAccomplishments(storedAccomplishments);

    const storedCertifications = JSON.parse(localStorage.getItem("certificationsList")) || [];
    setSavedCertifications(storedCertifications);

    const storedReferences = JSON.parse(localStorage.getItem("referencesList")) || [];
    setSavedReferences(storedReferences);

    const storedSoftwareSkills = JSON.parse(localStorage.getItem("softwareSkills")) || [];
    setSoftwareSkills(storedSoftwareSkills);

    const storedVolunteering = JSON.parse(localStorage.getItem("volunteering")) || [];
    setSavedVolunteering(storedVolunteering);

    const storedInterests = JSON.parse(localStorage.getItem("interests")) || [];
    setSavedInterests(storedInterests);

    const storedWebsites = JSON.parse(localStorage.getItem("websites")) || [];
    setSavedWebsites(storedWebsites);

    // Get all keys from localStorage that start with "custom_"
    const userId = localStorage.getItem("userId");
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      
      if (currentCustomSections.length > 0) {
        currentCustomSections.forEach(sectionName => {
          const userCustomKey = `custom_${userId}_${sectionName}`;
          try {
            const customSectionData = localStorage.getItem(userCustomKey);
            if (customSectionData) {
              const parsedData = JSON.parse(customSectionData);
              const items = parsedData.items || [];
              
              const validItems = items.filter(item => item && item.trim() !== '');
              
              if (validItems.length > 0) {
                sectionsData[sectionName] = validItems;
              }
            }
          } catch (error) {
            console.error(`Error loading current custom section ${sectionName}:`, error);
          }
        });
      }
    }

    setCustomSections(sectionsData);

    // Load saved section orders
    const savedLeftSectionOrder = localStorage.getItem("resume30LeftSectionOrder");
    const savedRightSectionOrder = localStorage.getItem("resume30RightSectionOrder");

    let leftOrder = savedLeftSectionOrder ? JSON.parse(savedLeftSectionOrder) : [...defaultLeftSectionOrder];
    let rightOrder = savedRightSectionOrder ? JSON.parse(savedRightSectionOrder) : [...defaultRightSectionOrder];

    // Ensure 'websites' is in one of the section lists
    const hasWebsites = leftOrder.includes('websites') || rightOrder.includes('websites');
    if (!hasWebsites) {
      leftOrder.push('websites');
    }

    setLeftSectionOrder(leftOrder);

    const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
    const filteredRightOrder = rightOrder.filter(section => !section.startsWith('custom_'));
    const allRightSections = [...filteredRightOrder, ...currentCustomSectionIds];
    setRightSectionOrder(allRightSections);
  }, []);

  // Save section orders to localStorage whenever they change
  useEffect(() => {
    if (leftSectionOrder.length > 0) {
      localStorage.setItem("resume30LeftSectionOrder", JSON.stringify(leftSectionOrder));
    }
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0) {
      localStorage.setItem("resume30RightSectionOrder", JSON.stringify(rightSectionOrder));
    }
  }, [rightSectionOrder]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId && Object.keys(customSections).length > 0) {
      const currentRightOrder = [...rightSectionOrder];
      const currentLeftOrder = [...leftSectionOrder];
      
      const currentCustomSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
      
      // Remove custom sections from both sides
      const filteredRightOrder = currentRightOrder.filter(section => !section.startsWith('custom_'));
      const filteredLeftOrder = currentLeftOrder.filter(section => !section.startsWith('custom_'));
      
      // Add custom sections to right side (or could add to left based on preference)
      const newRightOrder = [...filteredRightOrder, ...currentCustomSectionIds];
      
      if (JSON.stringify(newRightOrder) !== JSON.stringify(currentRightOrder)) {
        setRightSectionOrder(newRightOrder);
        localStorage.setItem("resume30RightSectionOrder", JSON.stringify(newRightOrder));
      }
      
      // Update left order if needed
      if (JSON.stringify(filteredLeftOrder) !== JSON.stringify(currentLeftOrder)) {
        setLeftSectionOrder(filteredLeftOrder);
        localStorage.setItem("resume30LeftSectionOrder", JSON.stringify(filteredLeftOrder));
      }
    } else if (userId && Object.keys(customSections).length === 0) {
      const currentRightOrder = [...rightSectionOrder];
      const currentLeftOrder = [...leftSectionOrder];
      
      const filteredRightOrder = currentRightOrder.filter(section => !section.startsWith('custom_'));
      const filteredLeftOrder = currentLeftOrder.filter(section => !section.startsWith('custom_'));
      
      if (filteredRightOrder.length !== currentRightOrder.length) {
        setRightSectionOrder(filteredRightOrder);
        localStorage.setItem("resume30RightSectionOrder", JSON.stringify(filteredRightOrder));
      }
      
      if (filteredLeftOrder.length !== currentLeftOrder.length) {
        setLeftSectionOrder(filteredLeftOrder);
        localStorage.setItem("resume30LeftSectionOrder", JSON.stringify(filteredLeftOrder));
      }
    }
  }, [customSections]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // Updated: Enhanced drag and drop handlers for bidirectional movement
  const handleLeftDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      index,
      side: 'left',
      sectionId: leftSectionOrder[index]
    }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      index,
      side: 'right',
      sectionId: rightSectionOrder[index]
    }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    const sourceSide = data.side;
    const sectionId = data.sectionId;

    // Remove from source array
    let newLeftSectionOrder = [...leftSectionOrder];
    let newRightSectionOrder = [...rightSectionOrder];
    
    if (sourceSide === 'left') {
      // Remove from left array if coming from left
      newLeftSectionOrder.splice(sourceIndex, 1);
    } else if (sourceSide === 'right') {
      // Remove from right array if coming from right
      newRightSectionOrder.splice(sourceIndex, 1);
    }
    
    // Add to target position in left array
    newLeftSectionOrder.splice(targetIndex, 0, sectionId);
    
    // Update both states
    setLeftSectionOrder(newLeftSectionOrder);
    setRightSectionOrder(newRightSectionOrder);
    
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    const sourceSide = data.side;
    const sectionId = data.sectionId;

    // Remove from source array
    let newLeftSectionOrder = [...leftSectionOrder];
    let newRightSectionOrder = [...rightSectionOrder];
    
    if (sourceSide === 'left') {
      // Remove from left array if coming from left
      newLeftSectionOrder.splice(sourceIndex, 1);
    } else if (sourceSide === 'right') {
      // Remove from right array if coming from right
      newRightSectionOrder.splice(sourceIndex, 1);
    }
    
    // Add to target position in right array
    newRightSectionOrder.splice(targetIndex, 0, sectionId);
    
    // Update both states
    setLeftSectionOrder(newLeftSectionOrder);
    setRightSectionOrder(newRightSectionOrder);
    
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetLeftSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
  };

  const resetRightSectionOrder = () => {
    const customSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
    const newDefaultRightOrder = [...defaultRightSectionOrder, ...customSectionIds];
    setRightSectionOrder(newDefaultRightOrder);
  };

  const resetAllSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    const customSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
    const newDefaultRightOrder = [...defaultRightSectionOrder, ...customSectionIds];
    setRightSectionOrder(newDefaultRightOrder);
  };

  const handleArrangeSectionsClick = () => {
    setArrangeDialogOpen(true);
  };

  const handleArrangeDialogClose = () => {
    setArrangeDialogOpen(false);
  };

  // Global font style for all text
  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
  };

  // Style object for consistent font family application
  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles
  });

  // Common section header style with lineWeight applied
  const getSectionHeaderStyle = () => ({
    ...globalFontStyle,
    fontSize: headingSize, 
    fontWeight: "bold", 
    marginBottom: `${sectionSpacing / 2}px`, 
    color: color,
    borderBottom: `${lineWeight}px solid ${color}`,
    paddingBottom: "8px",
  });

  // Common content spacing style
  const getContentSpacingStyle = () => ({
    marginBottom: `${sectionSpacing}px`,
    ...globalFontStyle
  });

  // Common paragraph spacing style
  const getParagraphSpacingStyle = () => ({
    marginBottom: `${paragraphSpacing}px`,
    ...globalFontStyle
  });

  // Common list item spacing style
  const getListItemSpacingStyle = () => ({
    marginBottom: `${paragraphSpacing / 2}px`,
    ...globalFontStyle
  });

  // Updated: Unified section renderer that works for both left and right sides
  const renderSection = (sectionId, isSidebar = false) => {
    const className = isSidebar ? "sidebar-section" : "resume-section";
    
    switch (sectionId) {
      case 'contact':
        return data.phone || data.email || data.city || data.linkedIn || data.website ? (
          <div key="contact" className={className} style={getContentSpacingStyle()}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={getSectionHeaderStyle()}
            >
              CONTACT
            </Typography>
            <Box sx={{ mb: `${sectionSpacing}px`, ...globalFontStyle, fontSize: `calc(${fontSize} * 0.93)` }}>
              {data.phone && (
                <Typography sx={{ mb: 1, ...globalFontStyle }}>
                  📞 {data.phone}
                </Typography>
              )}
              {data.email && (
                <Typography sx={{ mb: 1, ...globalFontStyle }}>
                  ✉️ {data.email}
                </Typography>
              )}
              {data.city && (
                <Typography sx={{ mb: 1, ...globalFontStyle }}>
                  📍 {data.city}
                </Typography>
              )}
              {data.website && (
                <Typography sx={{ mb: 1, ...globalFontStyle }}>
                  🌐 {data.website}
                </Typography>
              )}
              {data.linkedIn && (
                <Typography sx={{ mb: 1, display: 'flex', alignItems: 'center', ...globalFontStyle }}>
                  <LinkedInIcon sx={{ fontSize: 16, mr: 0.5 }} /> {data.linkedIn}
                </Typography>
              )}
            </Box>
          </div>
        ) : null;

      case 'education':
        return educationEntries.length > 0 ? (
          <div key="education" className={className} style={getContentSpacingStyle()}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={getSectionHeaderStyle()}
            >
              EDUCATION
            </Typography>
            <Box sx={{ width: '100%', mb: `${sectionSpacing}px`, ...globalFontStyle }}>
              {educationEntries.map((edu, index) => {
                const institutionName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
                const degreeName = edu.educationLevel === "school" ? edu.standard : edu.degreeName;
                const fieldOfStudy = edu.educationLevel === "school" ? edu.stream : edu.specialization;
                const location = edu.educationLevel === "school" ? edu.schoolLocation : edu.collegeLocation;
                const percentage = edu.percentage;
                const cgpa = edu.cgpa;
                const grade = edu.grade;
                const gradMonth = edu.gradMonth;
                const gradYear = edu.gradYear;
                
                return (
                  <Box key={edu.id} sx={{ mb: `${paragraphSpacing}px` }}>
                    <Typography sx={getTextStyle({ fontWeight: "bold", fontSize: `calc(${fontSize} * 0.93)` })}>
                      {degreeName}
                    </Typography>
                    <Typography sx={getTextStyle({ fontSize: `calc(${fontSize} * 0.85)` })}>
                      {institutionName}
                    </Typography>
                    <Typography sx={getTextStyle({ fontSize: `calc(${fontSize} * 0.85)`, mb: 2, color: "gray" })}>
                      {formatDate(gradMonth, gradYear)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </div>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <div key="languages" className={className} style={getContentSpacingStyle()}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={getSectionHeaderStyle()}
            >
              LANGUAGES
            </Typography>
            <Box sx={{ width: '100%', mb: `${sectionSpacing}px`, ...globalFontStyle }}>
              {savedLanguages.map((lang, index) => (
                <Typography
                  key={index}
                  sx={{ ...globalFontStyle, fontSize: `calc(${fontSize} * 0.93)`, mb: 1, display: 'flex', alignItems: 'center' }}
                >
                  <Box component="span" sx={{ width: 6, height: 6, bgcolor: color, borderRadius: '50%', mr: 1 }} />
                  {lang.name} {lang.level ? `(${lang.level})` : ""}
                </Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'skills':
        return (savedSkills.length > 0 || softwareSkills.length > 0) ? (
          <div key="skills" className={className} style={getContentSpacingStyle()}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={getSectionHeaderStyle()}
            >
              CORE SKILLS
            </Typography>
            <Box sx={{ width: '100%', mb: `${sectionSpacing}px`, ...globalFontStyle }}>
              {savedSkills.map((skill, index) => (
                <Typography
                  key={index}
                  sx={{ ...globalFontStyle, fontSize: `calc(${fontSize} * 0.93)`, mb: 1, display: 'flex', alignItems: 'center' }}
                >
                  <Box component="span" sx={{ width: 6, height: 6, bgcolor: color, borderRadius: '50%', mr: 1 }} />
                  {skill.name} {skill.rating ? `(${skill.rating}%)` : ""}
                </Typography>
              ))}
              {softwareSkills.map((skill, index) => (
                <Typography
                  key={`software-${index}`}
                  sx={{ ...globalFontStyle, fontSize: `calc(${fontSize} * 0.93)`, mb: 1, display: 'flex', alignItems: 'center' }}
                >
                  <Box component="span" sx={{ width: 6, height: 6, bgcolor: color, borderRadius: '50%', mr: 1 }} />
                  {skill.name} {skill.level ? `(${skill.level}%)` : ""}
                </Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <div key="accomplishments" className={className} style={getContentSpacingStyle()}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={getSectionHeaderStyle()}
            >
              AWARDS
            </Typography>
            <Box sx={{ width: '100%', mb: `${sectionSpacing}px`, ...globalFontStyle }}>
              {savedAccomplishments.map((acc, index) => (
                <Typography
                  key={index}
                  sx={{ ...globalFontStyle, fontSize: `calc(${fontSize} * 0.85)`, mb: 0.5 }}
                >
                  • {acc}
                </Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'profile':
        return (savedSummary || data.profile) ? (
          <div key="profile" className={className} style={getContentSpacingStyle()}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={getSectionHeaderStyle()}
            >
              PROFILE
            </Typography>
            {(savedSummary || data.profile).split(/\r?\n/).map((para, idx) => (
              para.trim() && (
                <Typography 
                  key={idx} 
                  style={{ 
                    fontFamily: font,
                    fontSize: fontSize,
                    fontStyle: fontStyle,
                    lineHeight: 1.6,
                    marginBottom: `${paragraphSpacing}px`, 
                    textAlign: "justify", 
                    textIndent: `${paragraphIndent}px`
                  }}
                >
                  {para}
                </Typography>
              )
            ))}
          </div>
        ) : null;

      case 'workExperience':
        return workExperiences && workExperiences.length > 0 ? (
          <div key="workExperience" className={className} style={getContentSpacingStyle()}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={getSectionHeaderStyle()}
            >
              WORK EXPERIENCE
            </Typography>
            
            {workExperiences.map((work, i) => (
              <Paper key={i} elevation={0} sx={{ 
                p: 2, 
                borderLeft: `${lineWeight}px solid ${color}`,
                mb: `${paragraphSpacing}px`, 
                bgcolor: "#f9f9f9",
                ...globalFontStyle 
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, ...globalFontStyle }}>
                  <Typography sx={{ fontWeight: "bold", ...globalFontStyle }}>{work.employer}</Typography>
                  <Typography sx={{ ...globalFontStyle, fontSize: `calc(${fontSize} * 0.85)`, color: "gray" }}>
                    {formatDate(work.startMonth, work.startYear)} -{" "}
                    {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 500, mb: 1, color: "#444", ...globalFontStyle }}>
                  {work.jobTitle}
                </Typography>
                {work.description && (
                  <ul style={{ margin: 0, paddingLeft: '20px', ...globalFontStyle, fontSize: `calc(${fontSize} * 0.93)` }}>
                    {work.description.split(/\r?\n/).map((item, idx) => (
                      item.trim() && <li key={idx} style={{ marginBottom: `calc(${paragraphSpacing}px * 0.15)`, ...globalFontStyle }}>{item.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1')}</li>
                    ))}
                  </ul>
                )}
              </Paper>
            ))}
          </div>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <div key="certifications" className={className} style={getContentSpacingStyle()}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={getSectionHeaderStyle()}
            >
              CERTIFICATIONS
            </Typography>
            {savedCertifications.map((cert, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px`, ...globalFontStyle }}>
                <Typography sx={getTextStyle({ fontWeight: "bold" })}>
                  {cert.name}
                </Typography>
                <Typography sx={getTextStyle()}>
                  {cert.provider} {cert.year && `(${cert.year})`}
                </Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <div key="volunteering" className={className} style={getContentSpacingStyle()}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={getSectionHeaderStyle()}
            >
              VOLUNTEERING
            </Typography>
            {savedVolunteering.map((vol, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px`, ...globalFontStyle }}>
                <Typography sx={getTextStyle({ fontWeight: "bold" })}>
                  {vol.subtopic}
                </Typography>
                <Typography sx={getTextStyle({ fontStyle: 'italic', color: '#666' })}>
                  {vol.fromDate} - {vol.toDate}
                </Typography>
                <Typography sx={getTextStyle()}>
                  {vol.content}
                </Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <div key="interests" className={className} style={getContentSpacingStyle()}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={getSectionHeaderStyle()}
            >
              INTERESTS
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', ...globalFontStyle }}>
              {savedInterests.map((interest, index) => (
                <Typography
                  key={index}
                  sx={getTextStyle({ mr: 2, mb: `${paragraphSpacing / 2}px` })}
                >
                  • {interest}
                </Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <div key="additionalInfo" className={className} style={getContentSpacingStyle()}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={getSectionHeaderStyle()}
            >
              ADDITIONAL INFORMATION
            </Typography>
            {savedAdditionalInfo.map((info, index) => (
              <Typography
                key={index}
                sx={getListItemSpacingStyle()}
              >
                • {info}
              </Typography>
            ))}
          </div>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <div key="references" className={className} style={getContentSpacingStyle()}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={getSectionHeaderStyle()}
            >
              REFERENCES
            </Typography>
            <Grid container spacing={2}>
              {savedReferences.slice(0, 2).map((ref, index) => (
                <Grid item xs={6} key={index}>
                  <Typography sx={{ fontWeight: "bold", ...globalFontStyle }}>
                    {ref.split(',')[0]}
                  </Typography>
                  <Typography sx={{ ...globalFontStyle, fontSize: `calc(${fontSize} * 0.85)` }}>
                    {ref.split(',')[1]}
                  </Typography>
                  <Typography sx={{ ...globalFontStyle, fontSize: `calc(${fontSize} * 0.85)` }}>
                    {ref.split(',')[2]}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </div>
        ) : null;

      case 'websites':
        return savedWebsites.length > 0 ? (
          <div key="websites" className={className} style={getContentSpacingStyle()}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={getSectionHeaderStyle()}
            >
              WEBSITES &amp; PROFILES
            </Typography>
            <Box sx={{ width: '100%', ...globalFontStyle }}>
              {savedWebsites.map((item, index) => {
                const label = typeof item === 'string' ? item : (item.label || item.name || item.title || '');
                const url = typeof item === 'object' ? (item.url || item.link || '') : '';
                return (
                  <Typography
                    key={index}
                    sx={{ ...globalFontStyle, fontSize: `calc(${fontSize} * 0.93)`, mb: 1, display: 'flex', alignItems: 'center' }}
                  >
                    <Box component="span" sx={{ width: 6, height: 6, bgcolor: color, borderRadius: '50%', mr: 1 }} />
                    {url && label !== url ? `${label}: ${url}` : (label || url)}
                  </Typography>
                );
              })}
            </Box>
          </div>
        ) : null;

      default:
        if (sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          const validItems = items.filter(item => item && item.trim() !== '');
          
          if (validItems.length > 0) {
            return (
              <div key={sectionId} className={className} style={getContentSpacingStyle()}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={getSectionHeaderStyle()}
                >
                  {customSectionName.replace(/_/g, ' ').toUpperCase()}
                </Typography>
                {validItems.map((item, i) => (
                  <Typography
                    key={i}
                    sx={getListItemSpacingStyle()}
                  >
                    • {item}
                  </Typography>
                ))}
              </div>
            );
          }
        }
        return null;
    }
  };

  // Updated: Unified rendering functions
  const renderSidebarSections = () => {
    return leftSectionOrder.map(sectionId => 
      renderSection(sectionId, true)
    ).filter(Boolean);
  };

  const renderRightContentSections = () => {
    return rightSectionOrder.map(sectionId => 
      renderSection(sectionId, false)
    ).filter(Boolean);
  };

  // Get section title for display
  const getSectionTitle = (sectionId) => {
    const titles = {
      'contact': 'Contact',
      'education': 'Education',
      'skills': 'Skills',
      'languages': 'Languages',
      'accomplishments': 'Awards',
      'profile': 'Profile',
      'workExperience': 'Work Experience',
      'certifications': 'Certifications',
      'volunteering': 'Volunteering',
      'interests': 'Interests',
      'additionalInfo': 'Additional Information',
      'references': 'References',
      'websites': 'Websites & Profiles'
    };
    
    if (sectionId.startsWith('custom_')) {
      return sectionId.replace('custom_', '').replace(/_/g, ' ').toUpperCase();
    }
    
    return titles[sectionId] || sectionId;
  };

  // Render drag panel for arrange sections dialog
  const renderDragPanel = (side, sections) => {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <DragIndicator sx={{ mr: 1 }} />
          {side === 'left' ? 'Left Sidebar' : 'Right Content'} Sections
          <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
            ({sections.length} sections)
          </Typography>
        </Typography>
        <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
          {sections.length > 0 ? (
            sections.map((sectionId, index) => (
              <Paper
                key={sectionId}
                draggable
                onDragStart={(e) => side === 'left' ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index, side)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => side === 'left' ? handleLeftDrop(e, index) : handleRightDrop(e, index)}
                onDragEnd={handleDragEnd}
                sx={{
                  p: 2,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: dragOverIndex === index && dragOverSide === side ? '#f0f0f0' : 'white',
                  boxShadow: dragOverIndex === index && dragOverSide === side ? 2 : 1,
                  cursor: 'grab',
                  '&:active': {
                    cursor: 'grabbing',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <DragIndicator color="action" />
                <Typography sx={{ ml: 1, flexGrow: 1 }}>
                  {getSectionTitle(sectionId)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {index + 1}
                </Typography>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
              No sections. Drag sections from the other side.
            </Typography>
          )}
        </Box>
        <Button 
          onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder} 
          variant="outlined" 
          size="small" 
          sx={{ mt: 1 }}
        >
          Reset {side === 'left' ? 'Left' : 'Right'} Order
        </Button>
      </Box>
    );
  };

  // Pagination for sidebar content
  const paginateSidebarContent = useCallback(() => {
    if (!sidebarContentRef.current) return;

    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 120;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;

    const sidebarContent = sidebarContentRef.current;
    const sections = Array.from(sidebarContent.querySelectorAll('.sidebar-section'));
    
    let newSidebarPages = [];
    let currentPageSections = [];
    let currentHeight = 0;
    
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      
      if (currentHeight + sectionHeight > availableHeight) {
        if (currentPageSections.length > 0) {
          newSidebarPages.push([...currentPageSections]);
        }
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      
      if (index === sections.length - 1 && currentPageSections.length > 0) {
        newSidebarPages.push([...currentPageSections]);
      }
    });
    
    if (newSidebarPages.length === 0) {
      newSidebarPages.push([]);
    }
    
    setSidebarPages(newSidebarPages);
  }, [page.height, topBottomMargin, leftSectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color, customSections]);

  // Pagination for main content
  const paginateMainContent = useCallback(() => {
    if (!rightContentRef.current) return;

    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 120;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;

    const rightContent = rightContentRef.current;
    const sections = Array.from(rightContent.querySelectorAll('.resume-section'));
    
    let newPages = [];
    let currentPageSections = [];
    let currentHeight = 0;
    
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      
      if (currentHeight + sectionHeight > availableHeight) {
        if (currentPageSections.length > 0) {
          newPages.push([...currentPageSections]);
        }
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      
      if (index === sections.length - 1 && currentPageSections.length > 0) {
        newPages.push([...currentPageSections]);
      }
    });
    
    if (newPages.length === 0) {
      newPages.push([]);
    }
    
    setPages(newPages);
  }, [page.height, topBottomMargin, rightSectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      paginateSidebarContent();
      paginateMainContent();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    paginateSidebarContent,
    paginateMainContent,
    educationEntries,
    savedSkills,
    savedSummary,
    workExperiences,
    savedAdditionalInfo,
    savedLanguages,
    savedAccomplishments,
    savedCertifications,
    savedReferences,
    softwareSkills,
    savedVolunteering,
    savedInterests,
    savedWebsites,
    customSections,
    leftSectionOrder,
    rightSectionOrder,
    sectionSpacing,
    paragraphSpacing,
    lineSpacing,
    fontSize,
    headingSize,
    font,
    color,
    lineWeight,
    formData,
  ]);

  // Render sidebar content for measurement
  const renderSidebarForMeasurement = () => (
    <Box ref={sidebarContentRef} sx={{ 
      visibility: 'hidden', 
      position: 'absolute', 
      top: -1000, 
      width: '30%',
      ...globalFontStyle 
    }}>
      {renderSidebarSections()}
    </Box>
  );

  // Render right content for measurement
  const renderRightContentForMeasurement = () => (
    <Box ref={rightContentRef} sx={{ 
      visibility: 'hidden', 
      position: 'absolute', 
      top: -1000, 
      width: '100%',
      ...globalFontStyle 
    }}>
      {renderRightContentSections()}
    </Box>
  );

  // Render header section
  const renderHeader = (pageIndex) => (
    pageIndex === 0 && (
      <Box 
        sx={{ 
          bgcolor: color, 
          color: "white", 
          p: 5, 
          textAlign: "left",
          "@media print": {
            bgcolor: color,
            WebkitPrintColorAdjust: "exact",
            printColorAdjust: "exact",
            marginLeft: `${sideMargins}px`,
            marginRight: `${sideMargins}px`,
            marginTop: `${topBottomMargin}px`,
          },
          ...globalFontStyle
        }}
      >
        <Typography 
          sx={{ 
            fontSize: "40px", 
            fontWeight: "900", 
            lineHeight: 1,
            fontFamily: font,
            mb: 1,
          }}
        >
          {data.firstName} {data.lastName}
        </Typography>
        <Typography 
          sx={{ 
            fontSize: "18px", 
            letterSpacing: 2, 
            opacity: 0.9,
            fontFamily: font,
            fontStyle: fontStyle,
          }}
        >
          {data.currentPosition}
        </Typography>
      </Box>
    )
  );

  const totalPages = Math.max(sidebarPages.length, pages.length, 1);

  return (
    <Box sx={{ width: "100%", position: "relative", m: 5 }}>
      {/* Page Navigation Controls with Arrange Sections Button */}
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, '@media print': { display: 'none' } }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2 }}>Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1} color="primary">
            <ChevronRight />
          </IconButton>
          
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 2 }}>
              <Button 
                onClick={handleArrangeSectionsClick}
                variant="contained" 
                startIcon={<Reorder />}
                color="primary"
              >
                Arrange Sections
              </Button>
              
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small">
                Reset All Sections
              </Button>
            </Box>
          )}
        </Box>
      )}
      
      {renderSidebarForMeasurement()}
      {renderRightContentForMeasurement()}
      
      <Box id="resume-container">
        {Array.from({ length: totalPages }).map((_, pageIndex) => (
          <Box
            key={pageIndex}
            className="resume-page"
            sx={{
              width: page.width,
              minHeight: page.height,
              mx: "auto",
              bgcolor: "#fff",
              boxShadow: pageIndex === currentPage ? 3 : 0,
              ...globalFontStyle,
              display: "flex",
              flexDirection: "column",
              pageBreakAfter: "always",
              marginTop: `${topBottomMargin}px`,
              marginBottom: `${topBottomMargin}px`,
              "@media print": {
                width: page.width,
                height: "auto",
                minHeight: page.height,
                boxShadow: "none",
                m: 0,
                marginTop: `${topBottomMargin}px`,
                marginBottom: `${topBottomMargin}px`,
                ...globalFontStyle
              },
              display: exporting ? 'flex' : (pageIndex === currentPage ? 'flex' : 'none'),
              '@media print': { display: 'flex' }
            }}
          >
            {renderHeader(pageIndex)}

            {/* Fixed Grid container - 30% sidebar, 70% main content */}
            <Grid container sx={{ 
              flex: 1, 
              width: '100%',
              margin: 0 
            }}>
              {/* Left Sidebar - 30% */}
              <Grid item xs={3.6} sx={{ 
                margin: 0,
                padding: 0,
                width: '30%',
                '@media print': {
                  width: '30%'
                }
              }}>
                <Box sx={{
                  width: '100%',
                  height: '100%',
                  p: `${topBottomMargin / 2}px ${sideMargins}px`,
                  bgcolor: "#f8f9fa",
                  boxSizing: 'border-box',
                  margin: 0,
                  "@media print": {
                    width: "100%",
                    height: "100%",
                    display: "block",
                    margin: 0,
                    padding: `${topBottomMargin / 2}px ${sideMargins}px`,
                  },
                  ...globalFontStyle
                }}>
                  {sidebarPages[pageIndex] ? (
                    <div 
                      style={{
                        ...globalFontStyle,
                        width: '100%',
                        margin: 0,
                        padding: 0
                      }}
                      dangerouslySetInnerHTML={{ 
                        __html: sidebarPages[pageIndex].join('')
                      }} 
                    />
                  ) : (
                    <Typography sx={getTextStyle()}></Typography>
                  )}
                </Box>
              </Grid>
              
              {/* Right Main Content - 70% */}
              <Grid item xs={8.4} sx={{ 
                margin: 0,
                padding: 0,
                width: '70%',
                '@media print': {
                  width: '70%'
                }
              }}>
                <Box sx={{ 
                  width: '100%',
                  height: '100%',
                  p: `${topBottomMargin / 2}px ${sideMargins}px`, 
                  bgcolor: "white",
                  boxSizing: 'border-box',
                  margin: 0
                }}>
                  {pages[pageIndex] ? (
                    <Box sx={{
                      width: '100%',
                      maxWidth: '100%',
                      ...globalFontStyle
                    }}>
                      <div 
                        style={{
                          width: '100%',
                          maxWidth: '100%',
                          ...globalFontStyle
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: pages[pageIndex].join('')
                        }} 
                      />
                    </Box>
                  ) : (
                    <Typography sx={getTextStyle()}></Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog
        open={arrangeDialogOpen}
        onClose={handleArrangeDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag and drop sections to reorder them within each side or move sections between left sidebar and right content.
            <br />
            <strong>Any section can be placed on either side!</strong>
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              width: "100%",
            }}
          >
            <Box sx={{ flex: 1, width: "50%" }}>
              {renderDragPanel("left", leftSectionOrder)}
            </Box>

            <Box sx={{ flex: 1, width: "50%" }}>
              {renderDragPanel("right", rightSectionOrder)}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">
            Reset All
          </Button>
          <Button onClick={handleArrangeDialogClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume30;